import React from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

// remark plugin: turn citation tokens like [1], [2,3] into links to #src-N
// (rendered as chips below). Plain [1] isn't markdown link syntax, so it
// arrives as text we can split.
function remarkCitations() {
  const re = /\[(\d+(?:\s*,\s*\d+)*)\]/g
  const walk = (node) => {
    if (!node.children) return
    const out = []
    for (const child of node.children) {
      if (child.type === 'text') {
        let last = 0
        let m
        re.lastIndex = 0
        while ((m = re.exec(child.value))) {
          if (m.index > last) out.push({ type: 'text', value: child.value.slice(last, m.index) })
          for (const n of m[1].split(',').map((s) => s.trim())) {
            out.push({
              type: 'link',
              url: `#src-${n}`,
              data: { hProperties: { className: 'citation', 'data-cite': n } },
              children: [{ type: 'text', value: n }],
            })
          }
          last = re.lastIndex
        }
        if (last < child.value.length) out.push({ type: 'text', value: child.value.slice(last) })
      } else {
        walk(child)
        out.push(child)
      }
    }
    node.children = out
  }
  return (tree) => walk(tree)
}

const components = {
  h1: ({ children }) => <h1 className="font-display text-2xl text-ink dark:text-mist mt-6 mb-2">{children}</h1>,
  h2: ({ children }) => (
    <h2 className="font-display text-xl text-ink dark:text-mist mt-6 mb-2 pb-1 border-b border-gold/30">{children}</h2>
  ),
  h3: ({ children }) => <h3 className="font-display text-lg text-ink dark:text-mist mt-4 mb-1.5">{children}</h3>,
  p: ({ children }) => <p className="text-[15px] leading-relaxed text-ink/90 dark:text-mist/90 my-2.5">{children}</p>,
  ul: ({ children }) => <ul className="list-disc pl-5 my-2.5 space-y-1 text-[15px] text-ink/90 dark:text-mist/90">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-5 my-2.5 space-y-1 text-[15px] text-ink/90 dark:text-mist/90">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-ink dark:text-mist">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-2 border-gold pl-3 my-3 italic text-ink-soft dark:text-mist/60">{children}</blockquote>
  ),
  hr: () => <hr className="my-4 border-0 h-px bg-gradient-to-r from-gold/50 to-transparent" />,
  a: ({ href, children }) => {
    if (href && href.startsWith('#src-')) {
      return (
        <a
          href={href}
          className="citation-chip inline-flex items-center justify-center align-super min-w-[16px] h-4 px-1 mx-px text-[10px] font-semibold rounded bg-gold/15 text-gold-deep dark:text-gold border border-gold/30 no-underline hover:bg-gold/30"
        >
          {children}
        </a>
      )
    }
    return (
      <a href={href} target="_blank" rel="noreferrer" className="text-spark-600 dark:text-spark underline underline-offset-2">
        {children}
      </a>
    )
  },
  code: ({ className, children }) =>
    /language-/.test(className || '') ? (
      // fenced block — <pre> provides the styling
      <code className="font-mono text-[13px]">{children}</code>
    ) : (
      <code className="rounded bg-parchment-200 dark:bg-midnight-700 px-1.5 py-0.5 text-[13px] font-mono text-ink dark:text-mist">
        {children}
      </code>
    ),
  pre: ({ children }) => (
    <pre className="my-3 rounded-lg bg-midnight-900 text-mist p-3.5 overflow-x-auto text-[13px] leading-relaxed border border-midnight-700">
      {children}
    </pre>
  ),
  table: ({ children }) => (
    <div className="my-3 overflow-x-auto">
      <table className="w-full text-sm border-collapse">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-parchment-200 dark:bg-midnight-700">{children}</thead>,
  th: ({ children }) => (
    <th className="border border-parchment-border dark:border-midnight-600 px-3 py-1.5 text-left font-semibold text-ink dark:text-mist">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border border-parchment-border dark:border-midnight-600 px-3 py-1.5 text-ink/90 dark:text-mist/90">{children}</td>
  ),
}

export default function Markdown({ children }) {
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm, remarkCitations]} components={components}>
      {children || ''}
    </ReactMarkdown>
  )
}
